import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import type { ContainerRow } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { moveCopy } from "./boo-app-copy";
import { DetailList, DetailRow, DisconnectButton, Notice } from "./ConnectionParts";
import type { BooApp } from "./use-boo-app";

// The Boo app of a chat platform on a connection page: header actions while
// this Boo holds it, and a body for each state (here, held elsewhere, in the
// workspace but unused, not installed).

export function BooAppActions({ app, container }: { app: BooApp; container: ContainerRow }) {
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  if (app.state !== "here") return null;
  const label = app.config.label;
  return (
    <>
      {app.canRelease && (
        <Button size="sm" variant="outline" disabled={app.release.isPending} onClick={() => setReleaseOpen(true)}>
          {app.release.isPending && <Loader2 className="size-3.5 animate-spin" />}
          Release
        </Button>
      )}
      {app.canDisconnect && (
        <DisconnectButton pending={app.disconnect.isPending} onClick={() => setDisconnectOpen(true)} />
      )}
      <ConfirmDialog
        open={releaseOpen}
        onOpenChange={setReleaseOpen}
        title={`Release ${label} from ${container.name}?`}
        description={`${container.name} will stop answering in ${label}, and another Boo can use it instead. ${label} stays connected to your organization.`}
        confirmLabel="Release"
        variant="default"
        loading={app.release.isPending}
        onConfirm={() => app.release.mutate(undefined, { onSuccess: () => setReleaseOpen(false) })}
      >
        {app.release.isError && <InlineError error={app.release.error} />}
      </ConfirmDialog>
      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title={`Disconnect ${label}?`}
        description={`This removes the Boo app from your ${app.config.placeNoun}. Messages from ${label} stop reaching this Boo.`}
        confirmLabel="Disconnect"
        loading={app.disconnect.isPending}
        onConfirm={() => app.disconnect.mutate(undefined, { onSuccess: () => setDisconnectOpen(false) })}
      >
        {app.disconnect.isError && <InlineError error={app.disconnect.error} />}
      </ConfirmDialog>
    </>
  );
}

export function BooAppPanel({
  app,
  usage,
  pitch,
}: {
  app: BooApp;
  // How people reach Boo once it's here.
  usage: string;
  // What adding it does, before it's installed.
  pitch: ReactNode;
}) {
  const [moveOpen, setMoveOpen] = useState(false);
  const { label, placeNoun } = app.config;
  const place = app.place ?? `your ${placeNoun}`;
  const move = moveCopy({ label, holderName: app.holderName, place });
  const moveButton = (onClick: () => void, text: string, primary = false) => (
    <Button size="sm" variant={primary ? "default" : "outline"} disabled={app.move.isPending} onClick={onClick}>
      {app.move.isPending && <Loader2 className="size-3.5 animate-spin" />}
      {text}
    </Button>
  );

  return (
    <div className="space-y-3">
      {app.state === "here" ? (
        <DetailList>
          <DetailRow term="Installed in">{place}</DetailRow>
          <DetailRow term="Reach Boo">{usage}</DetailRow>
        </DetailList>
      ) : app.state === "elsewhere" ? (
        <Notice
          tone="warning"
          action={app.canMoveHere && moveButton(() => setMoveOpen(true), move.button)}
        >
          <p>
            Boo in {place} answers through <strong>{app.holderName}</strong>. A {placeNoun} talks through one Boo
            at a time.
          </p>
          {!app.canMoveHere && app.canAttach && (
            <p className="text-xs text-muted-foreground">
              One of {app.holderName}&apos;s owners has to release it before it can move here.
            </p>
          )}
        </Notice>
      ) : app.state === "unattached" ? (
        <Notice tone="info" action={app.canMoveHere && moveButton(() => app.move.mutate(), "Use in this Boo", true)}>
          <p>Boo is in {place}, but no Boo answers there yet.</p>
        </Notice>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">{pitch}</div>
          {app.canInstall && (
            <Button size="sm" disabled={app.install.isPending} onClick={() => app.install.mutate()}>
              {app.install.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Add to {label}
            </Button>
          )}
        </div>
      )}
      {app.install.isError && <InlineError error={app.install.error} />}
      {app.move.isError && <InlineError error={app.move.error} />}
      <ConfirmDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        title={move.title}
        description={move.description}
        confirmLabel={move.confirmLabel}
        loading={app.move.isPending}
        onConfirm={() => app.move.mutate(undefined, { onSettled: () => setMoveOpen(false) })}
      />
    </div>
  );
}
